import {
  makeBridgeState,
  MakeBridgeStateParams,
} from "@ml-pa/react-apollo-form-tool/GenericBrigdeUtils/gerenericBridgeState";
import { GetCertificateToModifyByIdQuery } from "graphqlBase/certificates/__generated__/getCertificateToModifyById";
import { execGetUnitsMatrixParametersQuery } from "graphqlBase/units/__generated__/getUnitsMatrixParameters";
import { atom, selector, useRecoilCallback } from "recoil";
import { v4 as uuidv4 } from "uuid";
import {
  CerticateUploadProcessType,
  GridFormingFormValueGetter,
  ParsedSheets,
  UploadCertificate,
  UploadCertificateFile,
} from "../types";

// Define dummyIdPrefix locally since it's not exported from the standard upload
const dummyIdPrefix = "dummyId_";

// Form default keys that should be ignored when checking for user modifications
const formDefaultKeys = [
  "id",
  "__typename",
  "__unitToReplaceId",
  "__matrixField",
  "__matrixFieldType1",
  "__isLinked",
  "revision",
];
import { uploadCertificateFilesState } from "./uploadCertificateFileState";
import { activeUploadCertificateUnitIndexAtom, uploadCertificateUnitListAtom } from "./uploadCertificateUnitState";

import wait from "utils/wait";
import { getCertificateCreateFieldSelector } from "raft/CertificateForm";
import { userInfoAtom } from "lib/userInfo/state";
import { buildOriginalMatrixData } from "../validationRules/matrixParameterChangeDetection";
import { getGridFormingMatrixType } from "./matrixType";
import { isExtendedValiditySubmitBlockedAtom } from "pages/shared/state/extendedValidityState";

export { isExtendedValiditySubmitBlockedAtom } from "pages/shared/state/extendedValidityState";

// Combined type for certificate replacement state
export type CertificateReplacementState =
  | {
      certificateId: string;
      operation: "replace" | "extend";
    }
  | undefined;
export const uploadCertificateStateAtom = atom<UploadCertificate | undefined>({
  key: "uploadCertificateStateAtom",
  default: undefined,
});

export const hasTG8ConformityInEditAtom = atom<boolean | undefined>({
  key: "hasTG8ConformityInEditAtom",
  default: false,
});

export const isUploadCertificateLoadingAtom = atom<boolean>({
  key: "isUploadCertificateLoadingAtom",
  default: false,
});

export const isMatrixParametersLoadingAtom = atom<boolean>({
  key: "isMatrixParametersLoadingAtom",
  default: false,
});

/**
 * Stores the original matrix parameter values per unit (keyed by unit ID) as loaded from the backend.
 * Used to detect changes in the extended validity flow.
 */
export const unitMatrixDataOnRemoteAtom = atom<Record<string, { matrixField: string; matrixFieldType1: string }>>({
  key: "gridForming_unitMatrixDataOnRemoteAtom",
  default: {},
});

export const importedExcelOnExistingCertificate = atom<boolean>({
  key: "importedExcelOnExistingCertificate",
  default: false,
});

export const certicateUploadProcessTypeAtom = atom<CerticateUploadProcessType | undefined>({
  key: "certicateUploadProcessTypeAtom",
  default: undefined,
});

export const gridFormingMatrixTypeAtom = atom<number>({
  key: "gridFormingMatrixTypeAtom",
  default: 2,
});
export const certificateToReplaceIdAtom = atom<string | undefined>({
  key: "certificateToReplaceIdAtom",
  default: undefined,
});

export const isUploadValidExtendedByAtom = atom<boolean>({
  key: "isUploadValidExtendedByAtom",
  default: false,
});

export const isCertificateToReplaceOperationAtom = atom<boolean>({
  key: "isCertificateToReplaceOperationAtom",
  default: false,
});

export const certificateToEditIdAtom = atom<string | undefined>({
  key: "certificateToEditIdAtom",
  default: undefined,
});

export const certificateToEditForNavigationAtom = atom<string | undefined>({
  key: "certificateToEditForNavigationAtom",
  default: undefined,
});

// Combined atom for certificate replacement (new approach)
export const certificateReplacementAtom = atom<CertificateReplacementState>({
  key: "certificateReplacementAtom",
  default: undefined,
});

// Helper selector to get combined replacement state
export const certificateReplacementStateSelector = selector({
  key: "certificateReplacementStateSelector",
  get: ({ get }) => {
    const certificateId = get(certificateToReplaceIdAtom);
    const isExtension = get(isUploadValidExtendedByAtom);

    if (!certificateId) return undefined;

    return {
      certificateId,
      operation: isExtension ? ("extend" as const) : ("replace" as const),
    };
  },
});

export const certificOnRemoteAtom = atom<GetCertificateToModifyByIdQuery["certificate"] | undefined>({
  key: "certificOnRemoteAtom",
  default: undefined,
});

export const isImportGoingToOverwriteSelector = selector<boolean>({
  key: "isImportGoingToOverwriteSelector",
  get: ({ get }) => {
    const uploadCertificateState = get(uploadCertificateStateAtom);
    const uploadCertificateUnitsState = get(uploadCertificateUnitListAtom);
    const keys = Object.keys(uploadCertificateUnitsState[0] || {}).filter((key) => !formDefaultKeys.includes(key));
    if (
      Object.keys(uploadCertificateState || {}).length > 0 ||
      uploadCertificateUnitsState.length > 1 ||
      keys.length > 0
    ) {
      return true;
    }
    return false;
  },
});

const makeBridgeAtomState = (ids: string[]) => {
  const v = ids.reduce((acc, id) => {
    acc[id] = { outerId: id, isDeleted: false };
    return acc;
  }, {} as { [key: string]: { outerId: string; isDeleted: boolean } });
  return v;
};

export const certificateBridgeCertificateNormIssueDatesState = makeBridgeState({
  bridgeParams: { bridgeEntityName: "certificateBridgeCertificateNormIssueDates" },
} as MakeBridgeStateParams);

export const certificateBridgeSoftwareVersionState = makeBridgeState({
  bridgeParams: { bridgeEntityName: "certificateBridgeSoftwareVersions" },
} as MakeBridgeStateParams);

export const useImportValuesToCertificateUploadForm = (getFormValues: GridFormingFormValueGetter, isEdit = false) => {
  return useRecoilCallback(
    ({ set, reset, snapshot }) =>
      async (parsedSheets: ParsedSheets) => {
        const userInfo = await snapshot.getPromise(userInfoAtom);
        const role = userInfo?.role;
        if (!role) return;

        set(isUploadCertificateLoadingAtom, true);
        const formValues = await getFormValues({ parsedSheets });
        if (!formValues) {
          set(isUploadCertificateLoadingAtom, false);
          return;
        }
        const {
          certificateUpload,
          certificateUploadUnits: importedUnits,
          certificateBridgeCertificateNormIssueDates,
          certificateBridgeSoftwareVersions,
        } = formValues;

        const certificateUploadForRole = {
          ...certificateUpload,
          certificateHolderId: role === "MANUFACTURER" ? userInfo.tenantId : certificateUpload.certificateHolderId,
          certificateAuthorityId:
            role === "CERTIFICATION_AUTHORITY" ? userInfo.tenantId : certificateUpload.certificateAuthorityId,
        };

        // --- Merge logic for extension flow ---
        // If this is an extension flow, merge imported units with existing units (UI only)
        const replacementState = await snapshot.getPromise(certificateReplacementStateSelector);
        let finalUnits = importedUnits;
        if (replacementState?.operation === "extend") {
          const existingUnits = await snapshot.getPromise(uploadCertificateUnitListAtom);
          // Merge by modelName (or id if needed), keeping all unique units
          const existingUnitsMap = new Map(existingUnits.map((u) => [u.modelName || u.id, u]));
          importedUnits.forEach((u) => {
            existingUnitsMap.set(u.modelName || u.id, u);
          });
          finalUnits = Array.from(existingUnitsMap.values());
        }
        // --- End merge logic ---

        set(activeUploadCertificateUnitIndexAtom, -1);
        reset(uploadCertificateUnitListAtom);
        if (!isEdit) {
          reset(uploadCertificateStateAtom);
          reset(certificateBridgeCertificateNormIssueDatesState.state);
          reset(certificateBridgeSoftwareVersionState.state);

          set(uploadCertificateStateAtom, certificateUploadForRole);

          await wait(500);
          set(
            certificateBridgeCertificateNormIssueDatesState.state,
            makeBridgeAtomState(certificateBridgeCertificateNormIssueDates)
          );
          await wait(500);
          set(certificateBridgeSoftwareVersionState.state, makeBridgeAtomState(certificateBridgeSoftwareVersions));
        } else {
          set(importedExcelOnExistingCertificate, true);
        }
        set(uploadCertificateUnitListAtom, finalUnits);
        await wait(50);
        set(activeUploadCertificateUnitIndexAtom, 0);
        set(isUploadCertificateLoadingAtom, false);
      },
    []
  );
};

export const useSetValuesForCertificateToModify = (isEdit?: boolean, isUploadValidExtendedBy?: boolean) => {
  const __modifiedByUserAtom = getCertificateCreateFieldSelector({ formVersion: "matrixCertificateCreate" })(
    "__modifiedByUser"
  );
  return useRecoilCallback(
    ({ set, reset }) =>
      async (toModify: GetCertificateToModifyByIdQuery) => {
        if (!toModify.certificate) {
          set(isUploadCertificateLoadingAtom, false);
          return;
        }
        set(certificOnRemoteAtom, toModify.certificate);
        const {
          certificateBridgeCertificateNormIssueDates,
          certificateBridgeSoftwareVersions,
          certificateBridgeUnits,
          revision,
          ...certificateUploadPre
        } = toModify.certificate;
        if (certificateUploadPre.withTG8Rev25Conformity === true) {
          set(hasTG8ConformityInEditAtom, true);
        }

        const nextRevision = (revision ?? 0) + 1;
        const certificateUpload: UploadCertificate = {
          ...certificateUploadPre,
          id: undefined,
          __certificateToReplaceId: isEdit ? undefined : toModify.certificate.id,
          revision: isEdit ? revision : nextRevision,
          isVerified: false,
          __modifiedByUser: 0,
          certificateValidityStatusId: isEdit
            ? certificateUploadPre.certificateValidityStatusId
            : isUploadValidExtendedBy
            ? "c47a0b7c-701b-5544-92b0-6bcb48aac633" // valid
            : certificateUploadPre.certificateValidityStatusId,
        };

        const certificateUploadUnits =
          certificateBridgeUnits.map(({ unit }, index) => {
            // Exclude __typename, unitType, and grant access array fields (incompatible with RAFT form types)
            const { __typename, unitType, grantAccessToUnitParametersTenantIds, ...rest } = unit;
            // Use real ID for edit, extension, or replacement mode
            const isReplacement = !isEdit && !!toModify.certificate?.id; // Has certificate ID but not editing = replacement
            const shouldUseRealId = isEdit || isUploadValidExtendedBy || isReplacement;
            const assignedId = shouldUseRealId ? rest.id : `${dummyIdPrefix}${uuidv4()}`;

            return {
              ...rest,
              id: assignedId,
              __unitToReplaceId: unit.id,
              __matrixField: "", // Will be populated async below
              __matrixFieldType1: "", // Will be populated async below
              __isLinked: undefined,
              revision: nextRevision,
            };
          }) ?? [];

        const certificateBridgeSoftwareVersionsValues = certificateBridgeSoftwareVersions.map(
          ({ softwareVersionId }) => softwareVersionId
        );

        const certificateBridgeCertificateNormIssueDatesValues = certificateBridgeCertificateNormIssueDates.map(
          ({ certificateNormIssueDate }) => certificateNormIssueDate.id
        );

        set(activeUploadCertificateUnitIndexAtom, -1);
        reset(uploadCertificateUnitListAtom);
        reset(uploadCertificateStateAtom);
        reset(certificateBridgeCertificateNormIssueDatesState.state);
        reset(certificateBridgeSoftwareVersionState.state);

        set(uploadCertificateStateAtom, certificateUpload);

        set(uploadCertificateUnitListAtom, certificateUploadUnits);

        // Detect matrixType from the first unit and set the atom so the correct form is loaded
        const firstUnitMatrixType = certificateBridgeUnits[0]?.unit?.matrixType;
        set(gridFormingMatrixTypeAtom, getGridFormingMatrixType(firstUnitMatrixType));

        // Load matrix parameters for both edit and replacement scenarios
        if (!!toModify.certificate?.id) {
          set(isMatrixParametersLoadingAtom, true);

          // Collect all unit IDs that need matrix parameter loading
          const unitIdsForMatrixQuery: string[] = [];
          const unitIndexMap: Record<string, number> = {};

          certificateUploadUnits.forEach((unit, index) => {
            // For edit scenario, use the unit's actual ID; for replacement, use __unitToReplaceId
            const unitIdForMatrixQuery = isEdit ? unit.id : unit.__unitToReplaceId;
            if (unitIdForMatrixQuery) {
              unitIdsForMatrixQuery.push(unitIdForMatrixQuery);
              unitIndexMap[unitIdForMatrixQuery] = index;
            }
          });

          if (unitIdsForMatrixQuery.length > 0) {
            try {
              // Single batch query for all units
              const result = await execGetUnitsMatrixParametersQuery({
                variables: { unitIds: unitIdsForMatrixQuery },
              });

              if (result.data?.units?.items) {
                // Process each unit's matrix parameters (Type 2) and inertia parameters (Type 1)
                const matrixDataByUnitId: Record<string, string> = {};
                const inertiaDataByUnitId: Record<string, string> = {};

                result.data.units.items.forEach((unit) => {
                  if (unit.matrixParameters) {
                    const matrixParams = unit.matrixParameters;

                    // Create the cleaned matrix data in the same format as the component would
                    const cleanedData = {
                      zwValues: matrixParams.zwValues ?? [],
                      taMin: {
                        taValue: matrixParams.taMin?.taValue?.toString() ?? "",
                        pMinDyn: matrixParams.taMin?.pMinDyn?.toString() ?? "",
                        pMaxDyn: matrixParams.taMin?.pMaxDyn?.toString() ?? "",
                        pGrenzNegMin: matrixParams.taMin?.pGrenzNegMin?.toString() ?? "",
                        pGrenzNegMax: matrixParams.taMin?.pGrenzNegMax?.toString() ?? "",
                        pGrenzPosMin: matrixParams.taMin?.pGrenzPosMin?.toString() ?? "",
                        pGrenzPosMax: matrixParams.taMin?.pGrenzPosMax?.toString() ?? "",
                        effectiveImpedances: matrixParams.taMin?.effectiveImpedances ?? [],
                      },
                      taMax: {
                        taValue: matrixParams.taMax?.taValue?.toString() ?? "",
                        pMinDyn: matrixParams.taMax?.pMinDyn?.toString() ?? "",
                        pMaxDyn: matrixParams.taMax?.pMaxDyn?.toString() ?? "",
                        pGrenzNegMin: matrixParams.taMax?.pGrenzNegMin?.toString() ?? "",
                        pGrenzNegMax: matrixParams.taMax?.pGrenzNegMax?.toString() ?? "",
                        pGrenzPosMin: matrixParams.taMax?.pGrenzPosMin?.toString() ?? "",
                        pGrenzPosMax: matrixParams.taMax?.pGrenzPosMax?.toString() ?? "",
                        effectiveImpedances: matrixParams.taMax?.effectiveImpedances ?? [],
                      },
                      taRange:
                        matrixParams.taRange?.map((range) => ({
                          taValue: range.taValue?.toString() ?? "",
                          pMinDyn: range.pMinDyn?.toString() ?? "",
                          pMaxDyn: range.pMaxDyn?.toString() ?? "",
                          pGrenzNegMin: range.pGrenzNegMin?.toString() ?? "",
                          pGrenzNegMax: range.pGrenzNegMax?.toString() ?? "",
                          pGrenzPosMin: range.pGrenzPosMin?.toString() ?? "",
                          pGrenzPosMax: range.pGrenzPosMax?.toString() ?? "",
                          effectiveImpedances: range.effectiveImpedances ?? [],
                        })) ?? [],
                    };

                    matrixDataByUnitId[unit.id] = JSON.stringify(cleanedData);
                  }

                  if (unit.inertiaParameters) {
                    const inertiaParams = unit.inertiaParameters;
                    const cleanedData = {
                      taGen: inertiaParams.taGen,
                      taZus: inertiaParams.taZus,
                      taGes: inertiaParams.taGes,
                    };
                    inertiaDataByUnitId[unit.id] = JSON.stringify(cleanedData);
                  }
                });

                // Update all units at once with their matrix/inertia parameters
                set(uploadCertificateUnitListAtom, (currentUnits) => {
                  return currentUnits.map((currentUnit, unitIndex) => {
                    // For edit scenario, use the unit's actual ID; for replacement, use __unitToReplaceId
                    const unitIdForMatrixQuery = isEdit ? currentUnit.id : currentUnit.__unitToReplaceId;

                    if (unitIdForMatrixQuery) {
                      return {
                        ...currentUnit,
                        __matrixField: matrixDataByUnitId[unitIdForMatrixQuery] ?? currentUnit.__matrixField,
                        __matrixFieldType1: inertiaDataByUnitId[unitIdForMatrixQuery] ?? currentUnit.__matrixFieldType1,
                      };
                    }
                    return currentUnit;
                  });
                });

                // Store original matrix values for extended validity flow change detection
                const originalMatrixData = buildOriginalMatrixData({
                  units: certificateUploadUnits,
                  matrixDataByUnitId,
                  inertiaDataByUnitId,
                  isEdit,
                });
                set(unitMatrixDataOnRemoteAtom, originalMatrixData);
              }
            } catch (error) {
              console.warn(`Failed to load matrix parameters for units:`, error);
            }
          }

          set(isMatrixParametersLoadingAtom, false);
        }

        await wait(50);

        set(activeUploadCertificateUnitIndexAtom, 0);

        await wait(500);
        set(
          certificateBridgeCertificateNormIssueDatesState.state,
          makeBridgeAtomState(certificateBridgeCertificateNormIssueDatesValues)
        );
        await wait(500);
        set(certificateBridgeSoftwareVersionState.state, makeBridgeAtomState(certificateBridgeSoftwareVersionsValues));
        if (isEdit) {
          const exitistingFiles = (toModify.certificate.certificateFiles ?? []).reduce<UploadCertificateFile[]>(
            (files, file) => {
              if (!file) return files;
              return files.concat({
                ...file,
                base64FileString: "",
                certificateId: toModify.certificate?.id ?? "",
                size: 0,
              });
              return files;
            },
            []
          );
          await wait(300);
          set(uploadCertificateFilesState, exitistingFiles);
          await wait(300);
          set(__modifiedByUserAtom, 1);
        }

        set(isUploadCertificateLoadingAtom, false);
      },
    []
  );
};

export const useResetForm = () => {
  return useRecoilCallback(({ reset }) => () => {
    reset(uploadCertificateUnitListAtom);
    reset(uploadCertificateStateAtom);
    reset(certificateBridgeCertificateNormIssueDatesState.state);
    reset(certificateBridgeSoftwareVersionState.state);
    reset(activeUploadCertificateUnitIndexAtom);
    reset(uploadCertificateFilesState);
    reset(uploadCertificateUnitListAtom);
    reset(certicateUploadProcessTypeAtom);
    reset(isUploadCertificateLoadingAtom);
    reset(isExtendedValiditySubmitBlockedAtom);
    reset(unitMatrixDataOnRemoteAtom);
  });
};
